import AddressBookInput from '@/components/common/AddressBookInput'
import DialogActions from '@/components/common/DialogActions'
import NameInput from '@/components/common/NameInput'
import NetworkWarning from '@/components/new-safe/create/NetworkWarning'
import ErrorMessage from '@/components/tx/ErrorMessage'
import { getProposerErrorText } from '@/features/proposers/utils/proposerErrors'
import {
  addressIsNotSmartContract,
  encodeEIP1271Signature,
  usesV1DelegateEndpoint,
  signProposerDelegation,
  signProposerTypedDataForSafe,
} from '@/features/proposers/utils/utils'
import {
  PROPOSER_LABEL_PLACEHOLDER,
  SMART_CONTRACT_PROPOSER_ERROR,
  SMART_CONTRACT_PROPOSER_INFO,
} from '@/features/proposers/constants'
import { useDelegatorSelection } from '../hooks/useDelegatorSelection'
import { buildDelegationOrigin, createDelegationMessage } from '../services/delegationMessages'
import useChainId from '@/hooks/useChainId'
import { useCurrentChain } from '@/hooks/useChains'
import { useDelegateMutations } from '@safe-global/utils/hooks/useDelegateMutations'
import useSafeAddress from '@/hooks/useSafeAddress'
import useWallet from '@/hooks/wallets/useWallet'
import { SETTINGS_EVENTS, trackEvent } from '@/services/analytics'
import { getAssertedChainSigner } from '@/services/tx/tx-sender/sdk'
import { useAppDispatch } from '@/store'
import { upsertAddressBookEntries } from '@/store/addressBookSlice'
import { showNotification } from '@/store/notificationsSlice'
import { asError } from '@safe-global/utils/services/exceptions/utils'
import { shortenAddress } from '@safe-global/utils/utils/formatters'
import { sanitizeName } from '@safe-global/utils/validation/names'
import { addressIsNotCurrentSafe, addressIsNotOwner, addressIsNotReserved } from '@safe-global/utils/utils/validation'
import { XIcon } from 'lucide-react'
import { Alert, AlertDescription, AlertSeverityIcon } from '@safe-global/views/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@safe-global/views/components/ui/dialog'
import { Button } from '@safe-global/views/components/ui/button'
import { Separator } from '@safe-global/views/components/ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@safe-global/views/components/ui/tooltip'
import { Typography } from '@safe-global/views/components/ui/typography'
import {
  useDelegatesPostDelegateV1Mutation,
  type CreateDelegateDto,
} from '@safe-global/store/gateway/AUTO_GENERATED/delegates'
import { getDelegateTypedData } from '@safe-global/utils/services/delegates'
import { type BaseSyntheticEvent, useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm, type Validate } from 'react-hook-form'
import useSafeInfo from '@/hooks/useSafeInfo'
import SignerSelector from '@/components/common/SignerSelector'
import InfoIcon from '@safe-global/views/assets/images/notifications/info.svg'
import SignatureIcon from '@safe-global/views/assets/images/transactions/signature.svg'

type AddProposerProps = {
  onClose: () => void
  onSuccess: () => void
}

enum ProposerEntryFields {
  address = 'address',
  name = 'name',
}

type ProposerEntry = {
  [ProposerEntryFields.name]: string
  [ProposerEntryFields.address]: string
}

const AddProposer = ({ onClose, onSuccess }: AddProposerProps) => {
  const [error, setError] = useState<Error>()
  const [blockedReason, setBlockedReason] = useState<string>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [multiSigInitiated, setMultiSigInitiated] = useState<boolean>(false)
  const [addDelegateV1] = useDelegatesPostDelegateV1Mutation()
  const { addDelegate } = useDelegateMutations()
  const dispatch = useAppDispatch()

  const chainId = useChainId()
  const chain = useCurrentChain()
  const wallet = useWallet()
  const safeAddress = useSafeAddress()
  const { safe } = useSafeInfo()

  const {
    delegatorOptions,
    setSelectedDelegator,
    effectiveDelegator,
    parentSafeAddress,
    parentThreshold,
    parentOwners,
    isMultiSigRequired,
    isParentLoading,
  } = useDelegatorSelection()

  const methods = useForm<ProposerEntry>({
    defaultValues: {
      [ProposerEntryFields.address]: '',
      [ProposerEntryFields.name]: '',
    },
    mode: 'onChange',
  })

  const safeOwnerAddresses = useMemo(() => safe.owners.map((owner) => owner.value), [safe.owners])

  const validateAddress = useCallback<Validate<string>>(
    async (value) => {
      const notReserved = addressIsNotReserved('This proposer address is not valid')
      const notCurrentSafe = addressIsNotCurrentSafe(safeAddress, 'Cannot add Safe account itself as proposer')
      const notOwner = addressIsNotOwner(safeOwnerAddresses, 'Cannot add Safe Owner as proposer')
      const notSmartContract = addressIsNotSmartContract(chainId, SMART_CONTRACT_PROPOSER_ERROR)

      return notReserved(value) ?? notCurrentSafe(value) ?? notOwner(value) ?? (await notSmartContract(value))
    },
    [safeAddress, safeOwnerAddresses, chainId],
  )

  const { handleSubmit, formState } = methods

  const addressError = formState.errors[ProposerEntryFields.address]?.message
  const isSmartContractError = addressError === SMART_CONTRACT_PROPOSER_ERROR

  const onConfirm = handleSubmit(async (data: ProposerEntry) => {
    if (!wallet || !chain) return

    const name = sanitizeName(data.name)

    // The name is deliberately never sent to the backend; it lives only in this device's address book.
    const saveNameLocally = () =>
      dispatch(upsertAddressBookEntries({ chainIds: [chainId], address: data.address, name }))

    setError(undefined)
    setBlockedReason(undefined)
    setIsLoading(true)

    try {
      // Backstop in case the field validator has not settled by the time the form is submitted
      const smartContractError = await addressIsNotSmartContract(chainId, SMART_CONTRACT_PROPOSER_ERROR)(data.address)
      if (smartContractError) {
        setBlockedReason(smartContractError)
        return
      }

      const useV1Endpoint = usesV1DelegateEndpoint(chain, wallet)
      const signer = await getAssertedChainSigner(wallet.provider)

      let signature: string
      let delegator: string

      if (parentSafeAddress) {
        if (isMultiSigRequired) {
          // Multi-sig flow: create off-chain message on parent Safe for signature collection
          const eoaSignature = await signProposerTypedDataForSafe(
            chain,
            data.address,
            parentSafeAddress,
            safeAddress,
            'add',
            signer,
          )
          const delegateTypedData = getDelegateTypedData(chain, data.address, safeAddress, 'add')
          const origin = buildDelegationOrigin('add', data.address, safeAddress)

          await createDelegationMessage(dispatch, chainId, parentSafeAddress, delegateTypedData, eoaSignature, origin)

          saveNameLocally()
          setMultiSigInitiated(true)
          trackEvent(SETTINGS_EVENTS.PROPOSERS.SUBMIT_ADD_PROPOSER)
          setIsLoading(false)
          return
        }

        // Single-sig nested Safe owner: sign and submit immediately
        const eoaSignature = await signProposerTypedDataForSafe(
          chain,
          data.address,
          parentSafeAddress,
          safeAddress,
          'add',
          signer,
        )
        signature = await encodeEIP1271Signature(parentSafeAddress, eoaSignature)
        delegator = parentSafeAddress
      } else {
        signature = await signProposerDelegation({
          chain,
          wallet,
          proposerAddress: data.address,
          safeAddress,
          action: 'add',
          signer,
        })
        delegator = wallet.address
      }

      const createDelegateDto: CreateDelegateDto = {
        delegate: data.address,
        delegator,
        label: PROPOSER_LABEL_PLACEHOLDER,
        signature,
        safe: safeAddress,
      }

      if (useV1Endpoint && !parentSafeAddress) {
        await addDelegateV1({ chainId, createDelegateDto }).unwrap()
      } else {
        await addDelegate({ chain, createDelegateDto })
      }

      saveNameLocally()

      trackEvent(SETTINGS_EVENTS.PROPOSERS.SUBMIT_ADD_PROPOSER)

      dispatch(
        showNotification({
          variant: 'success',
          groupKey: 'add-proposer-success',
          title: 'Proposer added successfully!',
          message: `${shortenAddress(data.address)} can now suggest transactions for this account.`,
        }),
      )

      onSuccess()
    } catch (err) {
      setError(asError(err))
      return
    } finally {
      setIsLoading(false)
    }
  })

  const onSubmit = (e: BaseSyntheticEvent) => {
    e.stopPropagation()
    onConfirm(e)
  }

  const onCancel = () => {
    trackEvent(SETTINGS_EVENTS.PROPOSERS.CANCEL_ADD_PROPOSER)
    onClose()
  }

  if (multiSigInitiated) {
    return (
      <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent padding="none" showCloseButton={false}>
          <DialogHeader className="flex-row items-center justify-between">
            <DialogTitle>Signature collection initiated</DialogTitle>
            <Button variant="ghost" size="icon-sm" aria-label="close" onClick={onClose}>
              <XIcon />
            </Button>
          </DialogHeader>

          <Separator />

          <div className="p-4">
            <Alert variant="info" className="mb-4">
              <AlertSeverityIcon variant="info" />
              <AlertDescription>1 of {parentThreshold} signatures collected</AlertDescription>
            </Alert>

            <Typography variant="paragraph-small" className="mb-4 block">
              The delegation request has been created as an off-chain message on your parent Safe. Other owners of the
              parent Safe need to sign it before the proposer can be added.
            </Typography>

            <Typography variant="paragraph-small" color="muted">
              The other parent Safe owners can find and sign this pending delegation on the proposer settings page of
              this Safe.
            </Typography>
          </div>

          <Separator />

          {/* eslint-disable-next-line no-restricted-syntax -- p-6: bespoke footer padding around DialogActions (item A), no token */}
          <DialogFooter className="p-6">
            <DialogActions confirmLabel="Done" onConfirm={onClose} />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent padding="none" showCloseButton={false}>
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            <DialogHeader className="flex-row items-center justify-between">
              <DialogTitle data-testid="untrusted-token-warning">Add proposer</DialogTitle>

              <Button variant="ghost" size="icon-sm" aria-label="close" onClick={onCancel}>
                <XIcon />
              </Button>
            </DialogHeader>

            <Separator />

            <div className="p-4">
              {isMultiSigRequired && (
                <Alert variant="info" className="mb-4">
                  <AlertSeverityIcon variant="info" />
                  <AlertDescription>
                    This requires {parentThreshold} of {parentOwners?.length ?? '?'} parent Safe owner signatures to
                    complete.
                  </AlertDescription>
                </Alert>
              )}

              <div className="mb-4">
                <Typography variant="paragraph-small">
                  You&apos;re about to grant this address the ability to propose transactions. To complete the setup,
                  confirm with a signature from your connected wallet.
                </Typography>
              </div>

              <Alert variant="info">
                <AlertSeverityIcon variant="info" />
                <AlertDescription>
                  The proposer&apos;s address is publicly visible. The name is saved on this device only.
                </AlertDescription>
              </Alert>

              <div className="my-4">
                <AddressBookInput
                  name="address"
                  label="Address"
                  validate={validateAddress}
                  variant="outlined"
                  fullWidth
                  required
                />
              </div>

              <div className="mb-4">
                <NameInput name="name" label="Name" required inputSize="hero" />
              </div>

              {error && (
                <div className="mt-4">
                  <ErrorMessage error={error}>{getProposerErrorText(error, 'Error adding proposer')}</ErrorMessage>
                </div>
              )}

              {blockedReason && (
                <div className="mt-4">
                  <ErrorMessage>{blockedReason}</ErrorMessage>
                </div>
              )}

              <NetworkWarning action="sign" />

              {delegatorOptions.length > 1 && (
                <div className="mt-4">
                  <Typography variant="h4" className="mb-2 flex items-center gap-2">
                    <SignatureIcon className="size-4" />
                    Delegate as
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <span tabIndex={0} className="inline-flex">
                            <InfoIcon className="size-4 text-[var(--color-border-main)]" />
                          </span>
                        }
                      />
                      <TooltipContent>
                        Your connected wallet controls multiple Safe accounts that are owners of this Safe. Select which
                        account to create the proposer under.
                      </TooltipContent>
                    </Tooltip>
                  </Typography>

                  <SignerSelector
                    options={delegatorOptions}
                    value={effectiveDelegator}
                    onChange={setSelectedDelegator}
                    label="Delegator account"
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* eslint-disable-next-line no-restricted-syntax -- p-6: bespoke footer padding around DialogActions (item A), no token */}
            <DialogFooter className="p-6">
              <DialogActions
                onCancel={onCancel}
                confirmLabel="Continue"
                confirmTestId="submit-proposer-btn"
                confirmType="submit"
                confirmLoading={isLoading}
                confirmDisabled={isParentLoading || !formState.isValid}
                confirmCheckWallet={{ checkNetwork: !isLoading, allowProposer: false }}
                confirmTooltip={isSmartContractError ? SMART_CONTRACT_PROPOSER_INFO : undefined}
              />
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

export default AddProposer
